import React from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';

interface OffPageContentProps {
  onTooltipShow: (title: string, content: string) => void;
  onNavigate: (section: string) => void;
  darkMode?: boolean;
}

export default function OffPageContent({ onTooltipShow, onNavigate, darkMode }: OffPageContentProps) {
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: 'Implementation Hub / Off-Page Content',
    routePath: '/dashboard#off-page-content',
  });

  return (
    <div className={`space-y-6 ${darkMode ? 'dark text-white' : 'text-gray-900'}`}>
      {/* Header Area */}
      <div className="mb-4">
        <button
          onClick={() => onNavigate('implementation-overview')}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer border-none bg-transparent outline-none"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Off-Page Content</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Optimize and submit external elements to build domain authority.</p>
        </div>
      </div>

      {/* Hub Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-purple-50 dark:bg-purple-900/30 rounded-xl flex items-center justify-center border border-purple-100 dark:border-purple-800">
                <i className="ri-image-add-line text-2xl text-purple-600 dark:text-purple-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Image Submission</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Submit high-quality, compressed, keyword-rich images to high domain authority platforms (Pinterest, Flickr, Behance, and more) to boost SEO presence.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-magic-line text-purple-500"></i> AI Branded Images
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-folder-zip-line text-purple-500"></i> Metadata Packs
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('image-submission')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Classified Ads Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center border border-blue-100 dark:border-blue-800">
                <i className="ri-newspaper-line text-2xl text-blue-600 dark:text-blue-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Classified Ads</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  AI-generated business listings for high-DA classified platforms (Craigslist, Gumtree, OLX, Locanto, Kijiji, Oodle). Publish manually and track live URLs.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-sparkling-2-line text-blue-500"></i> AI Copy
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-download-2-line text-blue-500"></i> ZIP Download
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('classified-ads')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* PDF Submission Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center border border-blue-100 dark:border-blue-800">
                <i className="ri-file-pdf-2-line text-2xl text-blue-600 dark:text-blue-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">PDF Submission</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Create branded PDF documents with embedded backlinks and submit to document-sharing platforms (SlideShare, Scribd, Issuu, Academia.edu) to build domain authority.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-file-text-line text-blue-500"></i> AI PDF Generator
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-links-line text-blue-500"></i> Embedded Backlinks
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('pdf-submission')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
        {/* Web 2.0 / Article Submission Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-teal-50 dark:bg-teal-900/30 rounded-xl flex items-center justify-center border border-teal-100 dark:border-teal-800">
                <i className="ri-article-line text-2xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Web 2.0 / Article Submission</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  AI-generated, uniqueness-checked SEO articles for high-DA blogging platforms (Medium, WordPress.com, Blogger, Tumblr, and more).
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-sparkling-2-line text-teal-500"></i> AI Articles
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-shield-check-line text-teal-500"></i> Uniqueness Audit
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('web2-submission')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none">
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Social bookmarking card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-purple-50 dark:bg-purple-900/30 rounded-xl flex items-center justify-center border border-purple-100 dark:border-purple-800">
                <i className="ri-bookmark-line text-2xl text-purple-600 dark:text-purple-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Social Bookmarking</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Distribute your content across authoritative social bookmarking sites with AI-generated, keyword-optimized descriptions to strengthen backlinks and accelerate search engine discovery.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-sparkling-2-line text-purple-500"></i> AI Bookmark Summaries
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-shield-check-line text-purple-500"></i> High-DA Submission
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('social-bookmarking')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none">
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* business listing card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center border border-blue-100 dark:border-blue-800">
                <i className="ri-store-2-line text-2xl text-blue-600 dark:text-blue-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Profile/Business Listing</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Submit and manage your business information across trusted online directories and listing platforms to improve local visibility, brand credibility, and local SEO performance.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-sparkling-2-line text-blue-500"></i> Business Profiles
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-shield-check-line text-blue-500"></i> NAP Consistency
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('profile-business-listing')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none">
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* director submission card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-rose-50 dark:bg-rose-900/30 rounded-xl flex items-center justify-center border border-rose-100 dark:border-rose-800">
                <i className="ri-folder-open-line text-2xl text-rose-600 dark:text-rose-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Director Submission</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Submit your website to high-authority web directories with optimized business information to build quality backlinks, improve discoverability, and strengthen domain authority.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-sparkling-2-line text-rose-500"></i> Authority Directories
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-shield-check-line text-rose-500"></i> Smart Submission
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('directory-submission')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none">
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Q&A submission card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-teal-50 dark:bg-teal-900/30 rounded-xl flex items-center justify-center border border-teal-100 dark:border-teal-800">
                <i className="ri-question-answer-line text-2xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Q&A Submission</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Generate AI-powered answers and publish them on popular Q&A platforms with contextual backlinks to increase brand visibility, referral traffic, and topical authority.
                </p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-sparkling-2-line text-teal-500"></i> AI Generated Answers
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-shield-check-line text-teal-500"></i> Contextual Backlinks
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('qa-submission')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm cursor-pointer border-none">
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

